/**
 * Peer lifecycle management
 * @module Peer
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import * as Session from "./session";
import * as Sdp from "./sdp";
import * as Utils from "./utils";
import * as Bandwidth from "./bandwidth";
import createLog from "./log";
var log = createLog("peers");
var Transceivers = /** @class */ (function () {
    function Transceivers() {
        this.regularAudio = undefined;
        this.regularVideo = undefined;
        this.screenVideo = undefined;
        this.musicAudio = undefined;
    }
    return Transceivers;
}());
export { Transceivers };
var registeredPeers = new Map();
export function getByIdentifier(peerIdentifier) {
    return registeredPeers.get(peerIdentifier);
}
// Debug only
export function getAll() {
    return registeredPeers;
}
function limitBitrate() {
    var peers = Array.from(getAll().values());
    var peersCount = peers.length;
    log("Limiting bitrate because of peers count:", peersCount);
    var setLimit = function (peer) {
        var connection = peer.connection;
        log("Limiting bitrate for", peer.name, "type:", connection.localDescription.type);
        if (connection.localDescription.type === "offer") {
            connection
                .createOffer({
                offerToReceiveAudio: 1,
                offerToReceiveVideo: 1,
            })
                .then(function (e) {
                return connection.setLocalDescription(e);
            })
                .then(function () {
                var sdp = connection.remoteDescription.sdp;
                connection.setRemoteDescription(new RTCSessionDescription({
                    type: "answer",
                    sdp: Bandwidth.setMediaBitrate(sdp, "video"),
                }));
            })["catch"](function (error) {
                console.error(error);
                console.error("invalid sdp:", Bandwidth.setMediaBitrate(connection.remoteDescription.sdp, "video"));
            });
        }
        if ("answer" === connection.localDescription.type) {
            connection
                .setRemoteDescription(new RTCSessionDescription({
                type: "offer",
                sdp: Bandwidth.setMediaBitrate(connection.remoteDescription.sdp, "video"),
            }))
                .then(function () {
                return connection.createAnswer();
            })
                .then(function (e) {
                return connection.setLocalDescription(e);
            })["catch"](function (error) { return console.error("Error while limiting bandwidth and creating answer", error); });
        }
    };
    peers.forEach(function (peer) {
        if (peer.pendingLimit) {
            clearInterval(peer.pendingLimit);
        }
        var hasEmptyLocalDescription = function (_a) {
            var connection = _a.connection;
            if (!connection.localDescription)
                return true;
            return !connection.localDescription.type;
        };
        if (peer.connection.signalingState !== "stable" ||
            hasEmptyLocalDescription(peer) ||
            peer.connection.iceConnectionState.toString() !== "stable") {
            log("Connection not stable for", peer.name, " - waiting...");
            peer.pendingLimit = setInterval(function () {
                if (peer.connection.signalingState !== "stable" ||
                    hasEmptyLocalDescription(peer) ||
                    peer.connection.iceConnectionState.toString() !== "stable") {
                    return;
                }
                setLimit(peer);
                clearInterval(peer.pendingLimit);
            }, 250);
            return;
        }
        setLimit(peer);
    });
}
/**
 * Creates new RTCPeerConnection
 */
export function createConnection(session, peerIdentifier, peerName, reconnectCount, master, supportMusic, supportSimultaneousScreen) {
    var _this = this;
    var peer = {
        identifier: peerIdentifier,
        connection: new RTCPeerConnection({ iceServers: session.iceServers }),
        name: peerName,
        stream: null,
        isSuspended: false,
        onReady: function () { },
        pendingLimit: null,
        master: master,
        supportMusic: master && supportMusic,
        screenStream: null,
        supportSimultaneousScreen: supportSimultaneousScreen,
        transceivers: new Transceivers(),
        lastResult: null,
        isLocal: peerName === "local",
        localConnectionType: "",
        remoteConnectionType: "",
        reconnectCount: reconnectCount,
        bandwidthStatsTicker: null,
    };
    var connection = peer.connection;
    connection.onicecandidate = function (_a) {
        var candidate = _a.candidate;
        // REND-50: standard allows candidate.candidate to be empty string
        // but this causes Safari to throw parsing error. Workaround is to
        // not send empty string candidates (only FF send them).
        if (!candidate || !candidate.candidate)
            return;
        Session.send(session, "ice", {
            peersIdentifier: peerIdentifier,
            sdp: candidate.candidate,
            sdpMid: candidate.sdpMid,
            sdpMLineIndex: candidate.sdpMLineIndex,
        });
    };
    connection.onsignalingstatechange = function () {
        log("Signaling state for " + peer.name + ":", connection.signalingState);
    };
    connection.ontrack = function (event) {
        // Audio and video mids are present only for Wirecast Go compatibility.
        if ((event.transceiver.mid == "0" || event.transceiver.mid == "audio") || (event.transceiver.mid == "1" || event.transceiver.mid == "video")) {
            log("Adding remote stream for", peer.name);
            peer.stream = event.streams[0];
            peer.onReady();
            peer.onReady = function () { };
        }
        else {
            log("Adding remote screen stream for", peer.name);
            peer.screenStream = event.streams[0];
            event.streams[0].onremovetrack = function () {
                event.streams[0].onremovetrack = null;
                peer.screenStream = null;
            };
        }
    };
    registeredPeers.set(peerIdentifier, peer);
    log("Created connection for", peerName);
    // Bandwidth debug peer stats
    clearInterval(peer.bandwidthStatsTicker);
    peer.bandwidthStatsTicker = setInterval(function () { return __awaiter(_this, void 0, void 0, function () {
        var stats;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, connection.getStats(null)];
                case 1:
                    stats = _a.sent();
                    stats.forEach(function (report) {
                        var now = report.timestamp;
                        if (report.type === "outboundrtp" ||
                            report.type === "outbound-rtp" ||
                            (report.type === "ssrc" && report.bytesSent)) {
                            var bytes = report.bytesSent;
                            var packets = report.packetsSent;
                            if (peer.lastResult && peer.lastResult.get(report.id)) {
                                // calculate bitrate
                                var bitrate = (8 * (bytes - peer.lastResult.get(report.id).bytesSent)) /
                                    (now - peer.lastResult.get(report.id).timestamp);
                                if (bitrate && typeof peer.onBitrateChange === "function") {
                                    peer.onBitrateChange(bitrate.toFixed());
                                }
                            }
                        }
                    });
                    peer.lastResult = stats;
                    return [2 /*return*/];
            }
        });
    }); }, 1000);
    limitBitrate();
    return peer;
}
function setAnswererTransceivers(peer, localStream) {
    log("Setting answerer transceivers to connection", peer.name);
    var audioTrack = localStream.getAudioTracks()[0];
    var videoTrack = localStream.getVideoTracks()[0];
    // As an answerer, reuse already existing transceivers
    // Audio and video mids are present only for Wirecast Go compatibility.
    peer.transceivers.regularAudio = peer.connection.getTransceivers().find(function (t) { return t.mid == '0' || t.mid == 'audio'; });
    peer.transceivers.regularAudio.sender.setStreams(localStream);
    peer.transceivers.regularAudio.direction = "sendrecv";
    peer.transceivers.regularVideo = peer.connection.getTransceivers().find(function (t) { return t.mid == '1' || t.mid == 'video'; });
    peer.transceivers.regularVideo.sender.setStreams(localStream);
    peer.transceivers.regularVideo.direction = "sendrecv";
    if (audioTrack) {
        peer.transceivers.regularAudio.sender.replaceTrack(audioTrack);
    }
    if (videoTrack) {
        peer.transceivers.regularVideo.sender.replaceTrack(videoTrack);
    }
    else {
        peer.transceivers.regularVideo.sender.replaceTrack(Utils.getDummyVideoTrack());
    }
}
export function setAnswererTransceiversForMaster(peer, localStream) {
    if (!peer.master) {
        return;
    }
    setAnswererTransceivers(peer, localStream);
    // Add additional transceiver for peers supporting music audio - for now Wirecast only
    if (peer.supportMusic) {
        peer.transceivers.musicAudio = peer.connection.addTransceiver("audio", { direction: "inactive", streams: [localStream] });
    }
}
export function setOffererTransceiversForParticipant(peer, localStream) {
    if (peer.master) {
        return;
    }
    log("Setting transceivers to connection", peer.name);
    var audioTrack = localStream.getAudioTracks()[0];
    var videoTrack = localStream.getVideoTracks()[0];
    if (audioTrack) {
        peer.transceivers.regularAudio = peer.connection.addTransceiver(audioTrack, { direction: "sendrecv", streams: [localStream] });
    }
    else {
        peer.transceivers.regularAudio = peer.connection.addTransceiver("audio", { direction: "sendrecv", streams: [localStream] });
    }
    if (videoTrack) {
        peer.transceivers.regularVideo = peer.connection.addTransceiver(videoTrack, { direction: "sendrecv", streams: [localStream] });
    }
    else {
        peer.transceivers.regularVideo = peer.connection.addTransceiver(Utils.getDummyVideoTrack(), { direction: "sendrecv", streams: [localStream] });
    }
}
export function setAnswererTransceiversForParticipant(peer, localStream) {
    if (peer.master) {
        return;
    }
    setAnswererTransceivers(peer, localStream);
}
export function updateAudioTransceiver(peer, stream, audioProcessingDisabled) {
    if (peer.isLocal)
        return;
    if (typeof peer.connection.getTransceivers === "function") {
        var audioTrack = stream.getAudioTracks()[0];
        var regularAudioTransceiver = peer.transceivers.regularAudio;
        var enhancedAudioTransceiver = peer.transceivers.musicAudio;
        if (audioTrack && regularAudioTransceiver) {
            if (peer.master && enhancedAudioTransceiver) {
                if (audioProcessingDisabled) {
                    regularAudioTransceiver.sender.replaceTrack(null);
                    regularAudioTransceiver.direction = "recvonly";
                    enhancedAudioTransceiver.sender.replaceTrack(audioTrack);
                    enhancedAudioTransceiver.direction = "sendonly";
                }
                else {
                    regularAudioTransceiver.sender.replaceTrack(audioTrack);
                    regularAudioTransceiver.direction = "sendrecv";
                    enhancedAudioTransceiver.sender.replaceTrack(null);
                    enhancedAudioTransceiver.direction = "inactive";
                }
            }
            else {
                regularAudioTransceiver.sender.replaceTrack(audioTrack);
            }
        }
    }
}
export function updateVideoTransceiver(peer, stream) {
    if (typeof peer.connection.getTransceivers === "function") {
        var videoTrack = stream.getVideoTracks()[0];
        var videoTransceiver = peer.transceivers.regularVideo;
        if (videoTrack && videoTransceiver) {
            videoTransceiver.sender.replaceTrack(videoTrack);
        }
    }
}
export function updateScreenshareTransceiver(peer, stream, sharingScreenAndVideo) {
    if (typeof peer.connection.getTransceivers === "function") {
        if (sharingScreenAndVideo && stream && stream.getVideoTracks()[0]) {
            if (!peer.transceivers.screenVideo) {
                if (peer.master) {
                    peer.transceivers.screenVideo = peer.connection.addTransceiver(stream.getVideoTracks()[0], { direction: "sendonly" });
                }
                else {
                    peer.transceivers.screenVideo = peer.connection.addTransceiver(stream.getVideoTracks()[0], { direction: "sendonly", streams: [stream] });
                }
            }
            else {
                peer.transceivers.screenVideo.sender.replaceTrack(stream.getVideoTracks()[0]);
                peer.transceivers.screenVideo.direction = "sendonly";
            }
        }
        else {
            if (peer.transceivers.screenVideo) {
                peer.transceivers.screenVideo.sender.replaceTrack(null);
                peer.transceivers.screenVideo.direction = "recvonly";
            }
        }
    }
}
export function closeConnection(peer) {
    log("Closing connection", peer.name);
    // Stop bandwidth debug peer stats reports
    clearInterval(peer.bandwidthStatsTicker);
    peer.connection.close();
}
export function closeAll() {
    registeredPeers.forEach(function (value) { return closeConnection(value); });
    log("Closed all connections");
}
export function remove(peer) {
    log("Removing peer from registry", peer.name);
    registeredPeers["delete"](peer.identifier);
}
export function removeAll() {
    registeredPeers.clear();
    log("Removed all peers", registeredPeers);
}
export function setRemoteDescription(_a, description) {
    var connection = _a.connection;
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_b) {
            description.sdp = Bandwidth.setMediaBitrate(description.sdp, "video");
            description.sdp = Sdp.modifySdpForMusic(description.sdp);
            return [2 /*return*/, connection.setRemoteDescription(new RTCSessionDescription(description))];
        });
    });
}
export function createAnswer($socket, peer) {
    return __awaiter(this, void 0, void 0, function () {
        var answer;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, peer.connection.createAnswer()];
                case 1:
                    answer = _a.sent();
                    answer.sdp = Sdp.preferH264(answer.sdp);
                    answer.sdp = Bandwidth.setMediaBitrate(answer.sdp, "video");
                    return [4 /*yield*/, peer.connection.setLocalDescription(answer)];
                case 2:
                    _a.sent();
                    log("Creating answer", peer.name);
                    Session.send($socket.session, "createdAnswer", {
                        sdp: answer.sdp,
                        type: answer.type,
                        sessionGuid: $socket.session.sessionGuid,
                        peersIdentifier: peer.identifier,
                    });
                    return [2 /*return*/];
            }
        });
    });
}
export function createOffer(session, peer) {
    return __awaiter(this, void 0, void 0, function () {
        var offer;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, peer.connection.createOffer()];
                case 1:
                    offer = _a.sent();
                    offer.sdp = Sdp.preferH264(offer.sdp);
                    return [4 /*yield*/, peer.connection.setLocalDescription(offer)];
                case 2:
                    _a.sent();
                    log("Creating offer", peer.name);
                    Session.send(session, "peerMadeOffer", {
                        sdp: offer.sdp,
                        type: offer.type,
                        peersIdentifier: peer.identifier,
                    });
                    return [2 /*return*/];
            }
        });
    });
}
export function createCandidate(peer, candidateOptions) {
    return __awaiter(this, void 0, void 0, function () {
        var candidate, e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    candidate = new RTCIceCandidate({
                        sdpMLineIndex: candidateOptions.sdpMLineIndex,
                        sdpMid: candidateOptions.sdpMid,
                        candidate: candidateOptions.sdp,
                    });
                    log("Creating ICE candidate", peer.name);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, peer.connection.addIceCandidate(candidate)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    e_1 = _a.sent();
                    console.error("Adding ICE candidate failed", e_1, "candidate", candidate, "sdp", candidateOptions.sdp);
                    throw e_1;
                case 4: return [2 /*return*/];
            }
        });
    });
}



//////////////////
// WEBPACK FOOTER
// ./src/peers.ts
// module id = 28
// module chunks = 1